import type { ErrorClassification, HealthState, LogLevel, Severity } from '@monitor/contracts';
import { AlertOctagon, AlertTriangle, ArrowDownRight, ArrowUpRight, CheckCircle2, CircleDashed, CircleHelp, Inbox, Minus, X, XCircle } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { PROVIDER_MONOGRAM, PROVIDER_NAME, providerColor } from '../lib/color.js';
import { PROVIDER_LOGO } from '../lib/providerLogo.js';
import { pct } from '../lib/format.js';

const STATE_ICON: Record<string, ReactNode> = {
  Healthy: <CheckCircle2 size={12} />,
  Connected: <CheckCircle2 size={12} />,
  Succeeded: <CheckCircle2 size={12} />,
  Resolved: <CheckCircle2 size={12} />,
  Degraded: <AlertTriangle size={12} />,
  Acknowledged: <CircleDashed size={12} />,
  Unhealthy: <XCircle size={12} />,
  Disconnected: <XCircle size={12} />,
  Failed: <XCircle size={12} />,
  Open: <AlertOctagon size={12} />,
  Unknown: <CircleHelp size={12} />,
  NotConfigured: <CircleHelp size={12} />,
  Ignored: <Minus size={12} />,
};

const STATE_LABEL: Record<string, string> = {
  Healthy: 'Healthy',
  Degraded: 'Degraded',
  Unhealthy: 'Unhealthy',
  Unknown: 'Unknown',
  Connected: 'Connesso',
  Disconnected: 'Disconnesso',
  NotConfigured: 'Da configurare',
  Open: 'Aperto',
  Acknowledged: 'In carico',
  Resolved: 'Risolto',
  Succeeded: 'Riuscita',
  Failed: 'Fallita',
  Ignored: 'Ignorato',
};

/** A state is never color alone: icon + label, always. */
export function State({ value, compact, label, title }: { value: string; compact?: boolean; label?: string; title?: string }) {
  const visibleLabel = label ?? STATE_LABEL[value] ?? value;
  return (
    <span className={`state ${value}`} data-tip={title ?? visibleLabel}>
      {STATE_ICON[value]}
      {compact ? null : visibleLabel}
    </span>
  );
}

export const Dot = ({ state, title, silent }: { state: HealthState | string; title?: string; silent?: boolean }) => <span className={`dot ${state}`} data-tip={silent ? undefined : title ?? state} aria-label={title ?? state} />;

export const Sev = ({ value }: { value: Severity }) => <span className={`sev ${value}`}>{value}</span>;

const CLS_LABEL: Record<ErrorClassification, string> = {
  New: 'Nuovo',
  Spiking: 'In crescita',
  Regression: 'Regressione',
  Recurring: 'Ricorrente',
  Resolved: 'Risolto',
  Ignored: 'Ignorato',
};

export const Cls = ({ value }: { value: ErrorClassification }) => <span className={`cls ${value}`}>{CLS_LABEL[value]}</span>;

const LEVEL_SHORT: Record<LogLevel, string> = { Trace: 'TRC', Debug: 'DBG', Information: 'INF', Warning: 'WRN', Error: 'ERR', Critical: 'CRT' };

export const Lvl = ({ value }: { value: LogLevel }) => (
  <span className={`lvl ${value}`} data-tip={value}>
    {LEVEL_SHORT[value]}
  </span>
);

/** Delta vs previous period. `upIsBad` for costs, errors and latency. */
export function Delta({ value, upIsBad = true, digits = 0 }: { value: number | null; upIsBad?: boolean; digits?: number }) {
  if (value === null || !Number.isFinite(value)) return null;
  if (Math.abs(value) < 0.005) return <span className="delta flat">= 0%</span>;
  const up = value > 0;
  const bad = up === upIsBad;
  return (
    <span className={`delta ${bad ? 'bad' : 'good'}`}>
      {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {pct(Math.abs(value), digits)}
    </span>
  );
}

export function Panel({ title, icon, meta, action, children, flush, className, foot }: { title?: ReactNode; icon?: ReactNode; meta?: ReactNode; action?: ReactNode; children: ReactNode; flush?: boolean; className?: string; foot?: ReactNode }) {
  return (
    <section className={`panel ${className ?? ''}`}>
      {title ? (
        <header className="panel-head">
          <h2 className="panel-title">
            {icon}
            {title}
          </h2>
          {meta ? <span className="panel-meta">{meta}</span> : null}
          <span className="spacer" />
          {action}
        </header>
      ) : null}
      <div className={`panel-body${flush ? ' flush' : ''}`}>{children}</div>
      {foot ? <footer className="panel-foot">{foot}</footer> : null}
    </section>
  );
}

export function PageHead({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">{title}</h1>
        {subtitle ? <p className="page-subtitle">{subtitle}</p> : null}
      </div>
      {children ? <div className="page-actions">{children}</div> : null}
    </div>
  );
}

export function Segmented<T extends string | number>({ value, options, onChange, label }: { value: T; options: Array<{ value: T; label: ReactNode; count?: number }>; onChange: (value: T) => void; label?: string }) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button type="button" role="radio" aria-checked={option.value === value} key={String(option.value)} className={option.value === value ? 'on' : ''} onClick={() => onChange(option.value)}>
          {option.label}
          {option.count !== undefined ? <span className="count">{option.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Empty({ children, icon }: { children: ReactNode; icon?: ReactNode }) {
  return (
    <div className="empty">
      {icon ?? <Inbox size={22} />}
      {children}
    </div>
  );
}

export function Skeleton({ height = 16, width = '100%' }: { height?: number; width?: number | string }) {
  return <div className="skeleton" style={{ height, width }} />;
}

export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div className="stack" style={{ padding: 4 }}>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} height={index === 0 ? 60 : 180} />
      ))}
    </div>
  );
}

export function Failure({ error }: { error: unknown }) {
  return <div className="error-box">{error instanceof Error ? error.message : 'Errore inatteso'}</div>;
}

export function ProviderMark({ id, size = 18 }: { id: string; size?: number }) {
  return (
    <span className="provider-logo" style={{ width: size, height: size, fontSize: size * 0.42, borderRadius: size / 4, background: providerColor(id) }} aria-hidden="true">
      {PROVIDER_LOGO[id] ? (
        <svg viewBox="0 0 24 24" width={size * 0.62} height={size * 0.62} fill="currentColor">
          <path d={PROVIDER_LOGO[id]} />
        </svg>
      ) : (
        PROVIDER_MONOGRAM[id] ?? id.slice(0, 2)
      )}
    </span>
  );
}

export function ProviderChip({ id, name }: { id: string; name?: string }) {
  return (
    <span className="provider-chip">
      <span className="swatch" style={{ background: providerColor(id) }} />
      {name ?? PROVIDER_NAME[id] ?? id}
    </span>
  );
}

export const Env = ({ value }: { value: string }) => <span className={`env ${value}`}>{value === 'production' ? 'Prod' : value.charAt(0).toUpperCase() + value.slice(1)}</span>;

export function Tabs<T extends string>({ value, options, onChange }: { value: T; options: Array<{ value: T; label: string; count?: number }>; onChange: (value: T) => void }) {
  return (
    <div className="tabs" role="tablist">
      {options.map((option) => (
        <button type="button" role="tab" aria-selected={option.value === value} key={option.value} className={option.value === value ? 'on' : ''} onClick={() => onChange(option.value)}>
          {option.label}
          {option.count !== undefined ? <span className="count">{option.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Dialog({ title, onClose, children, foot, width }: { title: ReactNode; onClose: () => void; children: ReactNode; foot?: ReactNode; width?: number }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div className="backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true" style={width ? { width: `min(${width}px, 100%)` } : undefined}>
        <div className="dialog-head">
          <h2>{title}</h2>
          <span className="spacer" />
          <button type="button" className="btn ghost icon" onClick={onClose} aria-label="Chiudi">
            <X size={16} />
          </button>
        </div>
        <div className="dialog-body">{children}</div>
        {foot ? <div className="dialog-foot">{foot}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
